import { memo, useRef, useState, useEffect } from 'react';
import { Flex, Icon, Tooltip, useDisclosure } from '@chakra-ui/react';
import { MoreMenu } from './components';
import { PauseApplication } from './pause';
import { useGetApplicationActions } from './useGetActions';
import { AddToFlow } from './AddToFlow';
import { AddToProject } from './AddToProject';
import { EmailTemplates } from '../EmailTemplates';
import { Mail02 } from '@kula-ai/icons/dual-tone/Mail02';
import { useShowToast } from '@/hooks';
import { MoveToStage } from './MoveToStage';
import { SchedulingDrawer } from '@/pages/Scheduling/SchedulingDrawer';
import { OauthModal } from '../oauth';
import { useMailBox } from '@/hooks/use-mailbox';
import { useDispatch, useSelector } from 'react-redux';
import { selectUsers } from '@/store/users/selectors';
import { useSendEmailForApplicationMutation } from '@/store/query/ats-applications';
import { getInvalidVariableError } from '@/utils/commonHelpers';
import { getErrorMessage } from '@/utils/errors';
import { handleFraudDetectorError } from '@/utils/errors/Modules/fraud-detector';
import { useKFlags } from '@/hooks/use-k-flags';
import { Scheduler } from '@/pages/KInterviewScheduler/Scheduler';
import { SCHEDULE_TYPE_AI_ADHOC } from '@/pages/KInterviewScheduler/constant';
import { useGetAllUsers } from '@/hooks/useGetAllUsers';
import { SendAssessmentDrawer } from '../job-stage-actions/actions/assessment/send-assessment/SendAssessmentDrawer';
import { useAtsPermissions } from '@/hooks/useAtsPermissions';
import { KText } from '@/ui-library/components/text';
import { AddToCheckr } from './AddToCheckr';
import { AddToCertn, AddToZinc } from './background-check/AddToBackgroundCheck';
import {
  sendFraudDetectorVerification,
  isCreditCapError,
  getCreditCapData,
} from './background-check/backgroundCheckApi';
import { InsufficientCreditsModal } from './background-check/InsufficientCreditsModal';
import { AddToJob } from './AddToJob';
import { SendReviewDrawer } from '../job-stage-actions/actions/review/SendReviewDrawer.jsx';
import { CodingAssessmentDrawer } from '../job-stage-actions/actions/coding-assessment/assessment/CodingAssessmentDrawer';
import { SurveyDrawer } from '../job-stage-actions/actions/survey';
import { TransferToJob } from './TransferToJob';
import { CollectPhoneTimezoneModal } from '../job-stage-actions/actions/interview/ScheduleWithAi';
import { ModalWithForm } from '@/components/ats';
import OfferContainer from '@/components/contacts/candidate-job-drawer/tabs/current-job/details/OfferV1';
import { atsJobDetailsApiActions } from '@/store/atsJobDetails';
import { ATS_MILESTONES } from '@/ui-library/components/constants';
import { ScheduleInterview } from '@/pages/InterviewManager/ScheduleInterview';
import { KDrawer } from '@/ui-library/components';

const OfferActionContent = ({
  modalActions,
  applicationData,
  refetchApplication,
  toast,
}: any) => {
  const dispatch: any = useDispatch();
  const [isStageUpdating, setStageUpdating] = useState(false);

  const atsStages = applicationData?.ats_job_stages ?? [];
  const offerStages =
    atsStages?.filter(
      (i: any) => i?.ats_job_milestone?.name === ATS_MILESTONES.OFFER
    ) ?? [];
  const isOfferStage = offerStages.find(
    (stage: any) => stage?.id === applicationData?.job_stage_id
  );
  const [showOfferInitiation, setShowOfferInitiation] = useState(
    () => !!isOfferStage
  );
  const firstOfferStageId = offerStages?.sort(
    (a: any, b: any) => a?.position - b?.position
  )[0]?.id;

  useEffect(() => {
    if (modalActions.isOpen) {
      if (isOfferStage) {
        setShowOfferInitiation(true);
      } else {
        setShowOfferInitiation(false);
      }
    }
  }, [isOfferStage, modalActions.isOpen]);

  const handleMoveToOffer = async () => {
    setStageUpdating(true);
    try {
      const response: any = await dispatch(
        atsJobDetailsApiActions('moveToStage', {
          pathParams: { applicationId: applicationData?.id },
          requestConfig: {
            to_stage_id: firstOfferStageId,
          },
          isApi: true,
        })
      );

      if (response?.errors?.length) {
        toast({ title: 'Moving to offer stage failed', status: 'error' });
      } else {
        toast({ title: 'Moved to offer stage' });
        refetchApplication();
        setShowOfferInitiation(true);
      }
    } finally {
      setStageUpdating(false);
    }
  };

  if (!modalActions.isOpen) return null;

  return showOfferInitiation ? (
    <OfferContainer
      type={applicationData?.ats_offer?.id ? 'edit' : 'create'}
      inComingOffer={applicationData?.ats_offer}
      onClose={modalActions?.onClose}
      applicationData={applicationData}
      contact={applicationData?.contact || {}}
      onSuccess={() => {
        refetchApplication();
        modalActions?.onClose();
      }}
      redirectToSendForApprovalTab={false}
    />
  ) : (
    <ModalWithForm
      withForm={false}
      isOpen={true}
      onClose={modalActions?.onClose}
      headerText="Move to offer stage"
      subHeaderText="Confirm moving this candidate to the offer stage"
      primaryCtaText="Confirm"
      isCtaLoading={isStageUpdating}
      handleSubmit={handleMoveToOffer}
    />
  );
};

const ACTIONS = {
  PAUSE: 'pause',
  FLOW: 'flow',
  PROJECT: 'project',
  JOB: 'job',
  CHECKR: 'checkr',
  CERTN: 'certn',
  ZINC: 'zinc',
  INTERVIEW: 'interview',
  AI_ADHOC: 'aiAdhoc',
  SEND_ASSESSMENT: 'sendAssesment',
  SEND_HACKERRANK: 'sendHackerrank',
  SEND_HACKEREARTH: 'sendHackerearth',
  SEND_CRITERIA_CORP: 'sendCriteriaCorp',
  SEND_HACKERRANK_INTERVIEW: 'sendHackerrankInterview',
  CANDIDATE_SCHEDULE: 'candidateSchedule',
  SEND_ADHOC_REVIEW: 'sendAdhocReview',
  SEND_SURVEY: 'sendSurvey',
  SEND_HIREFLIX: 'sendHireflix',
  SEND_CODEINTERVIEW: 'sendCodeInterview',
  TRANSFER_TO_JOB: 'transferToJob',
  INITIATE_OFFER: 'initiateOffer',
  FRAUD_DETECTOR: 'fraudDetector',
};

interface ApplicationActionsProps {
  applicationData?: any;
  refetchApplication?: () => void;
  isRejected?: boolean;
  handlePushToHRIS?: (_selectedItrModel?: any) => void;
  isPending?: boolean;
  isFetchingForm?: boolean;
  activeIntegrationType?: string | null;
}

// eslint-disable-next-line react/display-name -- pre-existing; unrelated to the IM drawer-width change
export const ApplicationActions = memo(
  ({
    applicationData = {},
    refetchApplication,
    isRejected = false,
    handlePushToHRIS,
    isPending = false,
    isFetchingForm = false,
    activeIntegrationType = null,
  }: ApplicationActionsProps) => {
    const { contactsPermissions } = useAtsPermissions();

    const addEmailMessage =
      "Add or enrich this candidate's email address to send them emails";
    const askRecruiterToAddEmailMessage =
      "Ask a recruiter to add or enrich this candidate's email address to send them emails";

    const isEmailPresent = applicationData?.contact?.email;
    const canAddEmail = !isEmailPresent && contactsPermissions?.create;
    const disableEmail = !isEmailPresent && !contactsPermissions?.create;

    const tooltipLabel = canAddEmail
      ? addEmailMessage
      : disableEmail
        ? askRecruiterToAddEmailMessage
        : '';

    const {
      feScheduleRevamp,
      feHackerRank,
      feHackerEarth,
      feHackerankInterview,
      feCriteriaCorp,
      feCertn,
      feZinc,
      feHireflix,
      feRemoteInterview,
      feCoordinatorAgent,
      feSchedulingRevampV2,
    } = useKFlags();

    const [action, setAction] = useState('');
    const [creditCapError, setCreditCapError] = useState<any>(null);
    const moreMenuDisclosure = useDisclosure();
    const menuItems = useGetApplicationActions({
      isEmailPresent: applicationData?.contact?.email ?? false,
      handlePushToHRIS: handlePushToHRIS,
      isPending: Boolean(activeIntegrationType || isPending),
      isFetchingForm: isFetchingForm,
      activeIntegrationType: activeIntegrationType,
    });

    const { isCalConnected } = useMailBox();

    const modalActions = useDisclosure();
    const noPhoneTimezoneActions = useDisclosure();
    const sendEmailActions = useDisclosure();
    const oauthActions = useDisclosure();

    const contact = applicationData?.contact;
    const phone =
      contact?.local_phone_number?.trim?.() ||
      contact?.phone_number?.trim?.() ||
      '';
    const hasPhone = !!phone;

    const toast = useShowToast();
    const actionsContainerRef = useRef<HTMLDivElement | null>(null);
    const users = useSelector(selectUsers);

    const { ats_job } = applicationData || {};
    const { job_status } = ats_job || {};

    const isJobUpdateAllowedStatus = !['closed', 'archived']?.includes(
      job_status
    );

    const { processFromUserPayload, feSharedEmails } = useGetAllUsers();

    const [sendEmailForApplication, { isLoading: isSending }] =
      useSendEmailForApplicationMutation();
    const scheduleRef = useRef<any>(null);

    const handleSendEmail = async (values: any, onClose: any) => {
      try {
        let fromPayload = {};

        // todo: clean up once shared emails is tested
        if (feSharedEmails) {
          fromPayload = processFromUserPayload({
            selectedUser: values?.fromUserObject,
            payloadKey: 'sender_id',
          });
        } else {
          const { from } = values;
          const sender_id = users?.find((u) => u?.email === from)?.id;
          fromPayload = { sender_id };
        }

        const params = {
          ...values,
          ...fromPayload,
          ...(scheduleRef?.current?.submitCount > 0 && {
            schedule_at: scheduleRef?.current?.values?.schedule_at,
          }),
        };

        params.jobId = applicationData?.job_id;
        params.appIds = [applicationData?.id];
        const result: any = await sendEmailForApplication({
          contactId: applicationData?.contact?.id,
          params,
        });
        if ((result as any)?.error?.length) {
          toast({
            title:
              scheduleRef?.current?.submitCount > 0
                ? 'Scheduling email failed'
                : 'Sending email failed',
            status: 'error',
            description: getInvalidVariableError((result as any)?.error?.[0]),
          });
        } else {
          toast({
            title:
              scheduleRef?.current?.submitCount > 0
                ? 'Email scheduled successfully'
                : 'Email sent successfully',
          });
          refetchApplication();
          onClose();
        }
      } catch {
        toast({
          title: 'Sending email failed',
          status: 'error',
          description: 'Please try again',
        });
      }
    };

    const handleActionClick = async (actValue: string) => {
      if (
        (actValue === 'interview' ||
          actValue === 'candidateSchedule' ||
          actValue === 'aiAdhoc') &&
        !isCalConnected
      ) {
        oauthActions?.onOpen();
        return;
      }
      if (actValue === 'aiAdhoc' && !hasPhone) {
        noPhoneTimezoneActions.onOpen();
        return;
      }
      if (actValue === 'fraudDetector') {
        try {
          await sendFraudDetectorVerification({
            ats_application_id: applicationData?.id?.toString(),
            ats_job_id: applicationData?.job_id,
            verification_tier: 'BASIC',
          });
          toast({
            title: 'Fraud detector verification initiated successfully',
            status: 'success',
          });
          refetchApplication();
        } catch (error) {
          if (isCreditCapError(error)) {
            setCreditCapError(getCreditCapData(error));
          } else {
            toast({
              title: getErrorMessage(
                error,
                'fraudDetector',
                handleFraudDetectorError
              ),
              status: 'error',
            });
          }
        }
        return;
      }
      setAction(actValue);
      modalActions?.onOpen();
    };

    const modalClose = () => {
      setAction('');
      modalActions?.onClose();
    };

    const showInterviewSuccessToast = (apiReference: string) => {
      const msg = `Interview ${
        apiReference === 'create' ? 'created' : 'updated'
      } successfully`;
      toast({ title: msg });
    };

    return (
      <>
        <Flex
          gap="6px"
          alignItems="center"
          ref={actionsContainerRef}
          data-test-id="application-actions"
        >
          <MoveToStage
            applicationData={applicationData}
            refetch={refetchApplication}
            textProps={{ size: 'sm-semi-bold' }}
            showRejectAsButton={true}
            triggerReference="application"
            isRejected={isRejected}
          />
          <Flex
            border="1px solid"
            borderColor="border-color"
            borderRadius="6px"
            alignItems="center"
            height={'31px'}
          >
            {isJobUpdateAllowedStatus && (
              <Tooltip
                placement="bottom"
                label={
                  !applicationData?.contact?.email ? (
                    <Flex flexDir="column" p="5px">
                      <KText fontSize="sm" color={'base-white'} px="10px">
                        {tooltipLabel}
                      </KText>
                    </Flex>
                  ) : (
                    'Send email'
                  )
                }
                shouldWrapChildren
              >
                <Flex
                  onClick={
                    applicationData?.contact?.email && sendEmailActions?.onOpen
                  }
                  cursor={
                    applicationData?.contact?.email ? 'pointer' : 'not-allowed'
                  }
                  height="29px"
                  width="36px"
                  alignItems="center"
                  justifyContent="center"
                  _hover={{
                    bg: applicationData?.contact?.email
                      ? 'secondary-25'
                      : undefined,
                  }}
                  borderTopLeftRadius="6px"
                  borderBottomLeftRadius="6px"
                >
                  <Icon
                    as={Mail02}
                    width="18px"
                    height="18px"
                    color="secondary-700"
                  />
                </Flex>
              </Tooltip>
            )}
            <Flex bg="secondary-100" height="100%" width="1px" />

            {menuItems?.length > 0 && (
              <Flex
                as="button"
                height="100%"
                width="36px"
                alignItems="center"
                justifyContent="center"
                _hover={{ bg: 'secondary-25' }}
                borderTopRightRadius="6px"
                borderBottomRightRadius="6px"
              >
                <MoreMenu
                  enableTrippleDot={true}
                  handleActionClick={handleActionClick}
                  menuItems={menuItems}
                  isOpen={moreMenuDisclosure.isOpen}
                  onOpen={moreMenuDisclosure.onOpen}
                  onClose={moreMenuDisclosure.onClose}
                />
              </Flex>
            )}
          </Flex>
        </Flex>
        {modalActions?.isOpen && action === ACTIONS.PAUSE && (
          <PauseApplication
            modalActions={modalActions}
            modalClose={modalClose}
            applicationData={applicationData}
            refetchApplication={refetchApplication}
          />
        )}
        {modalActions?.isOpen && action === ACTIONS.FLOW && (
          <AddToFlow
            {...({
              modalActions,
              modalClose,
              contactId: applicationData?.contact?.id,
            } as any)}
          />
        )}
        {modalActions?.isOpen && action === ACTIONS.PROJECT && (
          <AddToProject
            {...({
              modalActions,
              modalClose,
              contactId: applicationData?.contact?.id,
            } as any)}
          />
        )}
        {modalActions?.isOpen && action === ACTIONS.JOB && (
          <AddToJob
            {...({
              modalActions,
              candidateId: [applicationData?.contact?.id],
              enableStageOption: true,
              triggerRef: 'job',
              selected: applicationData,
            } as any)}
          />
        )}
        {sendEmailActions?.isOpen && (
          <EmailTemplates
            formNameKey="sendTemplateId"
            mode="create"
            renderOnMount
            emailFormSubmit={handleSendEmail}
            modalConfig={{
              modalTitle: 'Send email',
              modalDescription:
                'Compose your email from scratch or use an email template',
            }}
            modalActions={sendEmailActions}
            isModalCtaLoading={isSending}
            isDrawer
            scheduleRef={scheduleRef}
            isScheduleEmail
          />
        )}
        {modalActions?.isOpen && action === ACTIONS.CHECKR && (
          <AddToCheckr
            modalActions={modalActions}
            contactDetails={applicationData}
            refetchApplication={refetchApplication}
          />
        )}
        {modalActions?.isOpen && action === ACTIONS.CERTN && feCertn && (
          <AddToCertn
            modalActions={modalActions}
            contactDetails={applicationData}
            refetchApplication={refetchApplication}
          />
        )}
        {modalActions?.isOpen && action === ACTIONS.ZINC && feZinc && (
          <AddToZinc
            modalActions={modalActions}
            contactDetails={applicationData}
            refetchApplication={refetchApplication}
          />
        )}
        {modalActions?.isOpen &&
          action === ACTIONS.INTERVIEW &&
          feSchedulingRevampV2 && (
            <KDrawer
              isOpen={true}
              onClose={modalActions?.onClose}
              handleOutsideClose={() => {}}
              variant="full"
              contentProps={{ maxW: '90%' }}
              skipHeader
              skipFooter
            >
              <ScheduleInterview
                mode="adhoc"
                applicationId={applicationData?.id}
                jobId={applicationData?.job_id}
                stageId={applicationData?.job_stage_id}
                candidateId={applicationData?.contact?.id}
                candidateName={
                  applicationData?.contact?.full_name ||
                  applicationData?.contact?.name ||
                  `${applicationData?.contact?.first_name || ''} ${applicationData?.contact?.last_name || ''}`.trim() ||
                  ''
                }
                candidateAvatar={
                  applicationData?.contact?.avatar_url ||
                  applicationData?.contact?.avatar_thumb_url ||
                  ''
                }
                candidateEmail={applicationData?.contact?.email || ''}
                jobTitle={
                  applicationData?.ats_job?.title ||
                  applicationData?.ats_job?.name ||
                  ''
                }
                applicationData={applicationData}
                onClose={modalActions?.onClose}
                onSuccess={() => {
                  refetchApplication();
                  modalActions?.onClose();
                }}
              />
            </KDrawer>
          )}
        {modalActions?.isOpen &&
          action === ACTIONS.INTERVIEW &&
          !feSchedulingRevampV2 &&
          feScheduleRevamp && (
            <Scheduler
              scheduleType="manual"
              isAdhoc={true}
              candidateId={applicationData?.contact?.id}
              jobId={applicationData?.job_id}
              stageId={applicationData?.job_stage_id}
              applicationId={applicationData?.id}
              closeModal={modalActions?.onClose}
              onSuccess={(apiReference: any) => {
                showInterviewSuccessToast(apiReference);
                refetchApplication();
                modalActions?.onClose();
              }}
              applicationData={applicationData}
            />
          )}
        {noPhoneTimezoneActions?.isOpen && (
          <CollectPhoneTimezoneModal
            isOpen={noPhoneTimezoneActions.isOpen}
            onClose={noPhoneTimezoneActions.onClose}
            applicationId={applicationData?.id ?? null}
            contact={contact}
            onSuccess={() => {
              refetchApplication();
              noPhoneTimezoneActions.onClose();
              setAction(ACTIONS.AI_ADHOC);
              modalActions?.onOpen();
            }}
          />
        )}
        {modalActions?.isOpen &&
          action === ACTIONS.AI_ADHOC &&
          feCoordinatorAgent && (
            <Scheduler
              scheduleType={SCHEDULE_TYPE_AI_ADHOC}
              isAdhoc={true}
              candidateId={applicationData?.contact?.id}
              jobId={applicationData?.job_id}
              stageId={applicationData?.job_stage_id}
              applicationId={applicationData?.id}
              closeModal={modalActions?.onClose}
              onSuccess={async () => {
                toast({
                  title: 'AI Coordinator dispatched',
                  status: 'success',
                });
                await refetchApplication?.();
                modalActions?.onClose();
                // Delayed refetch (same as manual ad-hoc SchedulingDrawer) so backend updates (e.g. active_sessions) are picked up
                setTimeout(() => {
                  refetchApplication?.();
                }, 3000);
              }}
              applicationData={applicationData}
            />
          )}
        {modalActions?.isOpen &&
          action === ACTIONS.INTERVIEW &&
          !feSchedulingRevampV2 &&
          !feScheduleRevamp && (
            <SchedulingDrawer
              isOpen={modalActions?.isOpen}
              onClose={modalActions?.onClose}
              actionId={null}
              candidateId={applicationData?.contact?.id}
              jobId={applicationData?.job_id}
              stageId={applicationData?.job_stage_id}
              applicationId={applicationData?.id}
              onSuccess={() => {
                refetchApplication();
                setTimeout(() => {
                  refetchApplication();
                }, 3000);
              }}
              type="manual"
            />
          )}
        {modalActions?.isOpen && action === ACTIONS.SEND_ASSESSMENT && (
          // Adhoc Assessment Drawer
          <SendAssessmentDrawer
            {...({
              isAdhoc: true,
              isOpen: modalActions?.isOpen,
              onClose: modalActions?.onClose,
              stageId: applicationData?.job_stage_id,
              application: applicationData,
              refetch: refetchApplication,
            } as any)}
          />
        )}
        {modalActions?.isOpen &&
          action === ACTIONS.SEND_HACKERRANK &&
          feHackerRank && (
            <CodingAssessmentDrawer
              isOpen={modalActions?.isOpen}
              onClose={modalActions?.onClose}
              activity={null}
              stageId={applicationData?.job_stage_id}
              application={applicationData}
              refetch={refetchApplication}
              isAdhoc={true}
              platform="hackerrank"
            />
          )}
        {modalActions?.isOpen &&
          action === ACTIONS.SEND_HACKEREARTH &&
          feHackerEarth && (
            <CodingAssessmentDrawer
              isOpen={modalActions?.isOpen}
              onClose={modalActions?.onClose}
              activity={null}
              stageId={applicationData?.job_stage_id}
              application={applicationData}
              refetch={refetchApplication}
              isAdhoc={true}
              platform="hackerearth"
            />
          )}
        {modalActions?.isOpen &&
          action === ACTIONS.SEND_CRITERIA_CORP &&
          feCriteriaCorp && (
            <CodingAssessmentDrawer
              isOpen={modalActions?.isOpen}
              onClose={modalActions?.onClose}
              activity={null}
              stageId={applicationData?.job_stage_id}
              application={applicationData}
              refetch={refetchApplication}
              isAdhoc={true}
              platform="criteriacorp"
            />
          )}
        {modalActions?.isOpen &&
          action === ACTIONS.SEND_HIREFLIX &&
          feHireflix && (
            <CodingAssessmentDrawer
              isOpen={modalActions?.isOpen}
              onClose={modalActions?.onClose}
              activity={null}
              stageId={applicationData?.job_stage_id}
              application={applicationData}
              refetch={refetchApplication}
              isAdhoc={true}
              platform="hireflix"
            />
          )}
        {modalActions?.isOpen &&
          action === ACTIONS.SEND_CODEINTERVIEW &&
          feRemoteInterview && (
            <CodingAssessmentDrawer
              {...({
                isOpen: modalActions?.isOpen,
                onClose: modalActions?.onClose,
                activity: null,
                stageId: applicationData?.job_stage_id,
                application: applicationData,
                refetch: refetchApplication,
                createMode: true,
                isAdhoc: true,
              } as any)}
              platform="codeinterview"
            />
          )}
        {modalActions?.isOpen &&
          action === ACTIONS.SEND_HACKERRANK_INTERVIEW &&
          feHackerankInterview &&
          feSchedulingRevampV2 && (
            <KDrawer
              isOpen={true}
              onClose={modalActions?.onClose}
              handleOutsideClose={() => {}}
              variant="full"
              contentProps={{ maxW: '90%' }}
              skipHeader
              skipFooter
            >
              <ScheduleInterview
                mode="adhoc"
                interviewPlatform="hackerrank"
                applicationId={applicationData?.id}
                jobId={applicationData?.job_id}
                stageId={applicationData?.job_stage_id}
                candidateId={applicationData?.contact?.id}
                candidateName={
                  applicationData?.contact?.full_name ||
                  applicationData?.contact?.name ||
                  `${applicationData?.contact?.first_name || ''} ${applicationData?.contact?.last_name || ''}`.trim() ||
                  ''
                }
                candidateAvatar={
                  applicationData?.contact?.avatar_url ||
                  applicationData?.contact?.avatar_thumb_url ||
                  ''
                }
                candidateEmail={applicationData?.contact?.email || ''}
                jobTitle={
                  applicationData?.ats_job?.title ||
                  applicationData?.ats_job?.name ||
                  ''
                }
                applicationData={applicationData}
                onClose={modalActions?.onClose}
                onSuccess={() => {
                  refetchApplication();
                  modalActions?.onClose();
                }}
              />
            </KDrawer>
          )}
        {modalActions?.isOpen &&
          action === ACTIONS.SEND_HACKERRANK_INTERVIEW &&
          feHackerankInterview &&
          !feSchedulingRevampV2 && (
            <Scheduler
              scheduleType="manual"
              isAdhoc={true}
              candidateId={applicationData?.contact?.id}
              jobId={applicationData?.job_id}
              stageId={applicationData?.job_stage_id}
              applicationId={applicationData?.id}
              closeModal={modalActions?.onClose}
              onSuccess={(apiReference: any) => {
                showInterviewSuccessToast(apiReference);
                refetchApplication();
                modalActions?.onClose();
              }}
              applicationData={applicationData}
              interviewPlatform="hackerrank"
            />
          )}
        {oauthActions?.isOpen && (
          <OauthModal
            isOpen={oauthActions?.isOpen}
            onClose={oauthActions?.onClose}
          />
        )}

        {modalActions?.isOpen &&
          action === ACTIONS.CANDIDATE_SCHEDULE &&
          feSchedulingRevampV2 && (
            <KDrawer
              isOpen={true}
              onClose={modalActions?.onClose}
              handleOutsideClose={() => {}}
              variant="full"
              contentProps={{ maxW: '90%' }}
              skipHeader
              skipFooter
            >
              <ScheduleInterview
                mode="self-schedule-adhoc"
                applicationId={applicationData?.id}
                jobId={applicationData?.job_id}
                stageId={applicationData?.job_stage_id}
                candidateId={applicationData?.contact?.id}
                candidateName={
                  applicationData?.contact?.full_name ||
                  applicationData?.contact?.name ||
                  `${applicationData?.contact?.first_name || ''} ${applicationData?.contact?.last_name || ''}`.trim() ||
                  ''
                }
                candidateAvatar={
                  applicationData?.contact?.avatar_url ||
                  applicationData?.contact?.avatar_thumb_url ||
                  ''
                }
                candidateEmail={applicationData?.contact?.email || ''}
                jobTitle={
                  applicationData?.ats_job?.title ||
                  applicationData?.ats_job?.name ||
                  ''
                }
                applicationData={applicationData}
                onClose={modalActions?.onClose}
                onSuccess={() => {
                  refetchApplication();
                  modalActions?.onClose();
                }}
              />
            </KDrawer>
          )}
        {modalActions?.isOpen &&
          action === ACTIONS.CANDIDATE_SCHEDULE &&
          !feSchedulingRevampV2 &&
          feScheduleRevamp && (
            <Scheduler
              scheduleType="self"
              isAdhoc={true}
              stageId={applicationData?.job_stage_id}
              candidateId={applicationData?.contact?.id}
              applicationId={applicationData?.id}
              jobId={applicationData?.job_id}
              closeModal={modalActions?.onClose}
              onSuccess={(apiReference: any) => {
                showInterviewSuccessToast(apiReference);
                refetchApplication();
                modalActions?.onClose();
              }}
              applicationData={applicationData}
            />
          )}
        {modalActions?.isOpen &&
          action === ACTIONS.CANDIDATE_SCHEDULE &&
          !feSchedulingRevampV2 &&
          !feScheduleRevamp && (
            <SchedulingDrawer
              isOpen={modalActions?.isOpen}
              onClose={modalActions?.onClose}
              actionId={null}
              candidateId={applicationData?.contact?.id}
              jobId={applicationData?.job_id}
              stageId={applicationData?.job_stage_id}
              applicationId={applicationData?.id}
              onSuccess={() => {
                refetchApplication();
              }}
              type="candidate"
            />
          )}
        {modalActions?.isOpen && action === ACTIONS.SEND_ASSESSMENT && (
          // Adhoc Assessment Drawer
          <SendAssessmentDrawer
            {...({
              isAdhoc: true,
              isOpen: modalActions?.isOpen,
              onClose: modalActions?.onClose,
              stageId: applicationData?.job_stage_id,
              application: applicationData,
              refetch: refetchApplication,
            } as any)}
          />
        )}
        {modalActions?.isOpen && action === ACTIONS.SEND_ADHOC_REVIEW && (
          // AdhocReview Drawer
          <SendReviewDrawer
            isAdhoc={true}
            isOpen={modalActions?.isOpen}
            onClose={modalActions?.onClose}
            application={applicationData}
            refetch={refetchApplication}
          />
        )}

        {modalActions?.isOpen && action === ACTIONS.SEND_SURVEY && (
          <SurveyDrawer
            isAdhoc={true}
            isOpen={modalActions?.isOpen}
            onClose={modalActions?.onClose}
            application={applicationData}
            stageId={applicationData?.job_stage_id}
            refetch={refetchApplication}
          />
        )}

        {oauthActions?.isOpen && (
          <OauthModal
            isOpen={oauthActions?.isOpen}
            onClose={oauthActions?.onClose}
          />
        )}
        {modalActions?.isOpen && action === ACTIONS.TRANSFER_TO_JOB && (
          <TransferToJob
            modalActions={modalActions}
            candidate={applicationData?.contact}
            applicationId={applicationData?.id}
            application={applicationData}
            onSuccess={() => {
              refetchApplication();
            }}
          />
        )}
        {modalActions?.isOpen && action === ACTIONS.INITIATE_OFFER && (
          <OfferActionContent
            modalActions={modalActions}
            applicationData={applicationData}
            refetchApplication={refetchApplication}
            toast={toast}
          />
        )}
        <InsufficientCreditsModal
          isOpen={!!creditCapError}
          onClose={() => setCreditCapError(null)}
          capError={creditCapError}
        />
      </>
    );
  }
);
